import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import PESService from 'App/services/PESService';
import { TextareaField } from 'Shared/form/TextareaField';
import SubmitButton from 'Shared/form/SubmitButton';
import { useQueryClient } from 'react-query';
import { stripAllHtml } from 'App/utils';
import styled from '@emotion/styled';
import FieldLabel from 'Shared/form/FieldLabel';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import CreatedByUser from './CreatedByUser';

const LabelArea = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 15px;

    .form-group {
        margin-bottom: 5px;
    }
`;

const validationSchema = yup.object().shape({
    note: yup.string().trim().max(2500, 'Cannot exceed 2500 character limit.').required('Note is required.'),
    important: yup.boolean(),
});

const upsert = async (values, patientId, uuid, isEditing, onHide, queryClient) => {
    if (isEditing) {
        const updatedNote = await PESService.updatePatientProfileNotes(patientId, uuid, values);
        queryClient.setQueryData(['patient', patientId, 'notes'], (oldNotes) => {
            return oldNotes.map((note) => (note.uuid === updatedNote.uuid ? updatedNote : note));
        });
    } else {
        const newNote = await PESService.addPatientProfileNotes(patientId, values);
        queryClient.setQueryData(['patient', patientId, 'notes'], (oldNotes) => {
            return oldNotes ? [...oldNotes, newNote] : [newNote];
        });
    }
    onHide();
};

const propTypes = {
    patientId: PropTypes.string.isRequired,
    onHide: PropTypes.func.isRequired,
    note: PropTypes.shape({
        note: PropTypes.string,
        important: PropTypes.bool,
        uuid: PropTypes.string,
    }),
};

const defaultProps = {
    note: null,
};

const AddEditNotesForm = ({ patientId, onHide, note }) => {
    const isEditing = !!note?.uuid;
    const initialValues = useMemo(() => {
        if (isEditing && note) {
            return {
                note: stripAllHtml(note.note),
                important: note.important,
            };
        }

        return {
            note: '',
            important: false,
        };
    }, [isEditing, note]);
    const queryClient = useQueryClient();

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={false}
            onSubmit={(values) => upsert(values, patientId, note?.uuid, isEditing, onHide, queryClient)}
        >
            {({ isSubmitting, handleSubmit, values, setFieldValue, handleBlur }) => {
                return (
                    <form onSubmit={handleSubmit} noValidate>
                        <Row>
                            <Col md={12}>
                                <LabelArea>
                                    <FieldLabel fieldName="note" required label="Note" />
                                    <span aria-label="Note Character Count">
                                        {values.note.trim().length}/{2500}
                                    </span>
                                </LabelArea>
                                <TextareaField
                                    required
                                    fieldName="note"
                                    disabled={isSubmitting}
                                    onBlur={(e) => {
                                        const trimmed = e.target.value.trim();
                                        setFieldValue('note', trimmed, true);
                                        handleBlur(e);
                                    }}
                                    rows={8}
                                />
                                <div
                                    style={{
                                        paddingTop: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <ToggleSwitchField
                                        fieldName="important"
                                        label="Mark as important"
                                        disabled={isSubmitting}
                                    />

                                    <CreatedByUser note={note} />
                                </div>
                                <FormButtonGroup>
                                    <Button
                                        variant="outline-secondary"
                                        disabled={isSubmitting}
                                        onClick={onHide}
                                        type="button"
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton>Save</SubmitButton>
                                </FormButtonGroup>
                            </Col>
                        </Row>
                    </form>
                );
            }}
        </Formik>
    );
};
AddEditNotesForm.propTypes = propTypes;
AddEditNotesForm.defaultProps = defaultProps;

export default AddEditNotesForm;
